// frontend/src/components/Portfolio/RegulatoryComplianceDashboard.tsx
// Comprehensive Regulatory Compliance Dashboard
// - TCFD alignment score
// - SEC climate disclosure readiness
// - EU Taxonomy alignment
// - Export-ready reports for regulators

import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Download as DownloadIcon,
  Assessment as AssessmentIcon,
  AccountBalance as GovernanceIcon,
  Gavel as RegulatoryIcon,
  Euro as EUIcon,
  Flag as USIcon,
  Public as GlobalIcon,
  ExpandMore as ExpandMoreIcon,
  Description as ReportIcon,
  Schedule as PendingIcon,
  TrendingUp as TrendingUpIcon,
  Security as SecurityIcon,
  Verified as VerifiedIcon,
} from '@mui/icons-material';
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

// ============================================================================
// INTERFACES
// ============================================================================
interface PortfolioItem {
  postal_code?: string;
  city?: string;
  state?: string;
  asset_value?: number | string;
  risk_score?: number;
  resilience?: number;
  com_risk?: number;
  [key: string]: any;
}

interface RegulatoryComplianceDashboardProps {
  portfolio: {
    items: PortfolioItem[];
    name?: string;
    total_value?: number;
    [key: string]: any;
  };
}

interface ComplianceRequirement {
  id: string;
  name: string;
  description: string;
  status: 'compliant' | 'partial' | 'non-compliant' | 'not-applicable';
  score: number;
  maxScore: number;
  details: string[];
  recommendations: string[];
}

interface Framework {
  id: string;
  name: string;
  fullName: string;
  icon: React.ReactNode;
  description: string;
  requirements: ComplianceRequirement[];
  overallScore: number;
  deadline?: string;
  mandatory: boolean;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================
const normalizeAssetValue = (value: any): number => {
  if (!value) return 0;
  if (typeof value === 'number') return value;
  const cleaned = String(value).replace(/[$,]/g, '');
  return parseFloat(cleaned) || 0;
};

const formatCurrency = (value: number): string => {
  if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
  if (value >= 1e3) return `$${(value / 1e3).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
};

const getStatusColor = (status: string): string => {
  switch (status) {
    case 'compliant': return '#10B981';
    case 'partial': return '#F59E0B';
    case 'non-compliant': return '#EF4444';
    default: return '#6B7280';
  }
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'compliant': return <CheckIcon sx={{ color: '#10B981' }} />;
    case 'partial': return <WarningIcon sx={{ color: '#F59E0B' }} />;
    case 'non-compliant': return <ErrorIcon sx={{ color: '#EF4444' }} />;
    default: return <InfoIcon sx={{ color: '#6B7280' }} />;
  }
};

const getScoreColor = (score: number): string => {
  if (score >= 80) return '#10B981';
  if (score >= 60) return '#F59E0B';
  if (score >= 40) return '#F97316';
  return '#EF4444';
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
export default function RegulatoryComplianceDashboard({ portfolio }: RegulatoryComplianceDashboardProps) {
  const [activeTab, setActiveTab] = useState(0);
  const [exportDialog, setExportDialog] = useState(false);
  const [selectedFramework, setSelectedFramework] = useState<Framework | null>(null);
  const [exportOptions, setExportOptions] = useState({
    tcfd: true,
    sec: true,
    euTaxonomy: false,
    includeRawData: false,
    format: 'pdf',
  });

  const items = portfolio?.items || [];
  const totalValue = portfolio?.total_value || items.reduce((sum, item) => sum + normalizeAssetValue(item.asset_value), 0);

  // ============================================================================
  // CALCULATE COMPLIANCE METRICS
  // ============================================================================
  const complianceMetrics = useMemo(() => {
    // Portfolio statistics for compliance calculations
    const avgRisk = items.length > 0 
      ? items.reduce((sum, item) => sum + parseFloat(String(item.risk_score || 0)), 0) / items.length 
      : 0;
    
    const highRiskCount = items.filter(item => parseFloat(String(item.risk_score || 0)) > 6).length;
    const highRiskValue = items
      .filter(item => parseFloat(String(item.risk_score || 0)) > 6)
      .reduce((sum, item) => sum + normalizeAssetValue(item.asset_value), 0);
    
    const hasResilienceData = items.some(item => item.resilience !== undefined);
    const hasHazardData = items.some(item => item.flood !== undefined || item.hurricane !== undefined);
    const hasValueData = items.some(item => normalizeAssetValue(item.asset_value) > 0);
    
    // Geographic diversity
    const states = new Set(items.map(item => item.state).filter(Boolean));
    const geographicDiversity = states.size;

    return {
      avgRisk,
      highRiskCount,
      highRiskValue,
      hasResilienceData,
      hasHazardData,
      hasValueData,
      geographicDiversity,
      totalLocations: items.length,
      totalValue,
    };
  }, [items, totalValue]);

  // ============================================================================
  // TCFD FRAMEWORK
  // ============================================================================
  const tcfdFramework = useMemo((): Framework => {
    const requirements: ComplianceRequirement[] = [
      {
        id: 'tcfd-governance-1',
        name: 'Board Oversight',
        description: 'Board oversight of climate-related risks and opportunities',
        status: complianceMetrics.hasHazardData ? 'compliant' : 'partial',
        score: complianceMetrics.hasHazardData ? 100 : 50,
        maxScore: 100,
        details: [
          'Climate risk data integrated into portfolio analysis',
          complianceMetrics.hasHazardData ? '18 hazard types tracked' : 'Limited hazard data available',
        ],
        recommendations: complianceMetrics.hasHazardData ? [] : ['Ensure all hazard data is populated for complete oversight'],
      },
      {
        id: 'tcfd-governance-2',
        name: 'Management Role',
        description: "Management's role in assessing and managing climate risks",
        status: complianceMetrics.totalLocations > 0 ? 'compliant' : 'non-compliant',
        score: complianceMetrics.totalLocations > 0 ? 100 : 0,
        maxScore: 100,
        details: [
          `${complianceMetrics.totalLocations} locations under active monitoring`,
          'Real-time risk scoring enabled',
        ],
        recommendations: [],
      },
      {
        id: 'tcfd-strategy-1',
        name: 'Physical Risk Identification',
        description: 'Identification of climate-related physical risks',
        status: complianceMetrics.hasHazardData ? 'compliant' : 'partial',
        score: complianceMetrics.hasHazardData ? 100 : 40,
        maxScore: 100,
        details: [
          'Acute risks: Hurricane, Flood, Wildfire, Tornado tracked',
          'Chronic risks: Heat, Drought, Sea Level Rise tracked',
        ],
        recommendations: [],
      },
      {
        id: 'tcfd-strategy-2',
        name: 'Scenario Analysis',
        description: 'Climate scenario analysis (1.5°C, 2°C, 4°C)',
        status: 'compliant',
        score: 100,
        maxScore: 100,
        details: [
          'SSP1-2.6 (1.5-2.0°C) scenario modeled',
          'SSP2-4.5 (2.5-3.0°C) scenario modeled',
          'SSP5-8.5 (4.0-5.0°C) scenario modeled',
        ],
        recommendations: [],
      },
      {
        id: 'tcfd-risk-1',
        name: 'Risk Identification Process',
        description: 'Process for identifying climate-related risks',
        status: complianceMetrics.hasHazardData ? 'compliant' : 'partial',
        score: complianceMetrics.hasHazardData ? 90 : 50,
        maxScore: 100,
        details: [
          'Automated risk scoring for all locations',
          `${complianceMetrics.highRiskCount} high-risk locations identified`,
        ],
        recommendations: complianceMetrics.highRiskCount > 0 ? ['Review mitigation strategies for high-risk assets'] : [],
      },
      {
        id: 'tcfd-risk-2',
        name: 'Risk Management Integration',
        description: 'Integration into overall risk management',
        status: complianceMetrics.hasValueData ? 'compliant' : 'partial',
        score: complianceMetrics.hasValueData ? 85 : 40,
        maxScore: 100,
        details: [
          `${formatCurrency(complianceMetrics.highRiskValue)} value at high risk`,
          'Risk-weighted asset analysis available',
        ],
        recommendations: !complianceMetrics.hasValueData ? ['Add asset values for complete risk-value analysis'] : [],
      },
      {
        id: 'tcfd-metrics-1',
        name: 'Climate Metrics',
        description: 'Metrics used to assess climate risks',
        status: 'compliant',
        score: 95,
        maxScore: 100,
        details: [
          '18 climate hazard metrics tracked',
          'Risk scores on 0-10 scale',
          'Value-at-risk calculations',
        ],
        recommendations: [],
      },
      {
        id: 'tcfd-metrics-2',
        name: 'Targets & Progress',
        description: 'Targets for managing climate risks and progress',
        status: 'partial',
        score: 60,
        maxScore: 100,
        details: [
          'Risk reduction targets can be set',
          'Geographic diversification tracked',
        ],
        recommendations: ['Set explicit climate risk reduction targets', 'Document progress toward targets'],
      },
    ];

    const totalScore = requirements.reduce((sum, r) => sum + r.score, 0);
    const maxScore = requirements.reduce((sum, r) => sum + r.maxScore, 0);
    const overallScore = Math.round((totalScore / maxScore) * 100);

    return {
      id: 'tcfd',
      name: 'TCFD',
      fullName: 'Task Force on Climate-related Financial Disclosures',
      icon: <GlobalIcon />,
      description: 'Global framework for climate-related financial risk disclosures, now incorporated into IFRS S2',
      requirements,
      overallScore,
      deadline: 'Annual reporting',
      mandatory: true,
    };
  }, [complianceMetrics]);

  // ============================================================================
  // SEC CLIMATE DISCLOSURE
  // ============================================================================
  const secFramework = useMemo((): Framework => {
    const requirements: ComplianceRequirement[] = [
      {
        id: 'sec-governance',
        name: 'Governance Disclosure',
        description: 'Board and management oversight of climate risks',
        status: complianceMetrics.totalLocations > 0 ? 'compliant' : 'partial',
        score: complianceMetrics.totalLocations > 0 ? 90 : 40,
        maxScore: 100,
        details: [
          'Portfolio-level risk monitoring in place',
          'Management dashboards available',
        ],
        recommendations: [],
      },
      {
        id: 'sec-strategy',
        name: 'Strategy Disclosure',
        description: 'Climate-related risks and their impacts on strategy',
        status: 'compliant',
        score: 85,
        maxScore: 100,
        details: [
          'Physical risk assessment completed',
          'Scenario analysis available (1.5°C, 2°C, 4°C)',
          'Financial impact projections calculated',
        ],
        recommendations: ['Document transition risk assessment'],
      },
      {
        id: 'sec-risk-management',
        name: 'Risk Management',
        description: 'Processes for managing climate-related risks',
        status: complianceMetrics.hasHazardData ? 'compliant' : 'partial',
        score: complianceMetrics.hasHazardData ? 90 : 50,
        maxScore: 100,
        details: [
          'Multi-hazard risk framework implemented',
          'Real-time monitoring capabilities',
          `${complianceMetrics.geographicDiversity} states covered for diversification`,
        ],
        recommendations: [],
      },
      {
        id: 'sec-metrics',
        name: 'Metrics & Targets',
        description: 'Climate-related metrics and targets',
        status: 'partial',
        score: 70,
        maxScore: 100,
        details: [
          'Risk metrics: 18 hazard types quantified',
          'Value-at-risk calculations available',
        ],
        recommendations: ['Add GHG emissions scope data if applicable', 'Set quantitative risk reduction targets'],
      },
      {
        id: 'sec-financial-statement',
        name: 'Financial Statement Impact',
        description: 'Climate-related impacts on financial statements',
        status: complianceMetrics.hasValueData ? 'compliant' : 'partial',
        score: complianceMetrics.hasValueData ? 80 : 30,
        maxScore: 100,
        details: [
          complianceMetrics.hasValueData ? `${formatCurrency(totalValue)} total portfolio value tracked` : 'Asset values needed',
          'Estimated annual loss calculations available',
        ],
        recommendations: !complianceMetrics.hasValueData ? ['Add asset values for financial impact analysis'] : [],
      },
      {
        id: 'sec-attestation',
        name: 'Attestation Readiness',
        description: 'Third-party attestation requirements (Large Accelerated Filers)',
        status: 'partial',
        score: 50,
        maxScore: 100,
        details: [
          'Data audit trail maintained',
          'Methodology documentation available',
        ],
        recommendations: ['Prepare for third-party data verification', 'Document data sources and methodologies'],
      },
    ];

    const totalScore = requirements.reduce((sum, r) => sum + r.score, 0);
    const maxScore = requirements.reduce((sum, r) => sum + r.maxScore, 0);
    const overallScore = Math.round((totalScore / maxScore) * 100);

    return {
      id: 'sec',
      name: 'SEC Climate',
      fullName: 'SEC Climate Disclosure Rules',
      icon: <USIcon />,
      description: 'U.S. Securities and Exchange Commission climate disclosure requirements for public companies',
      requirements,
      overallScore,
      deadline: 'FY 2025 (Large Accelerated Filers)',
      mandatory: true,
    };
  }, [complianceMetrics, totalValue]);

  // ============================================================================
  // EU TAXONOMY
  // ============================================================================
  const euTaxonomyFramework = useMemo((): Framework => {
    const requirements: ComplianceRequirement[] = [
      {
        id: 'eu-climate-mitigation',
        name: 'Climate Change Mitigation',
        description: 'Substantial contribution to climate change mitigation',
        status: 'partial',
        score: 40,
        maxScore: 100,
        details: [
          'Physical risk reduction tracking available',
          'Portfolio optimization for risk reduction possible',
        ],
        recommendations: ['Document specific mitigation activities', 'Track emissions reduction if applicable'],
      },
      {
        id: 'eu-climate-adaptation',
        name: 'Climate Change Adaptation',
        description: 'Substantial contribution to climate change adaptation',
        status: complianceMetrics.hasResilienceData ? 'compliant' : 'partial',
        score: complianceMetrics.hasResilienceData ? 80 : 50,
        maxScore: 100,
        details: [
          'Climate risk assessment completed',
          complianceMetrics.hasResilienceData ? 'Community resilience data integrated' : 'Resilience data partially available',
          'Adaptation planning capabilities available',
        ],
        recommendations: ['Document adaptation investments', 'Track resilience improvements over time'],
      },
      {
        id: 'eu-dnsh',
        name: 'Do No Significant Harm (DNSH)',
        description: 'No significant harm to other environmental objectives',
        status: 'partial',
        score: 55,
        maxScore: 100,
        details: [
          'Physical risk data available for assessment',
          'Multi-hazard framework supports DNSH analysis',
        ],
        recommendations: [
          'Document water use impacts',
          'Assess circular economy alignment',
          'Evaluate biodiversity impacts',
        ],
      },
      {
        id: 'eu-minimum-safeguards',
        name: 'Minimum Safeguards',
        description: 'Compliance with OECD Guidelines and UN Guiding Principles',
        status: 'not-applicable',
        score: 0,
        maxScore: 100,
        details: [
          'Requires organizational policy documentation',
          'Outside scope of climate risk platform',
        ],
        recommendations: ['Ensure organizational compliance with OECD/UN guidelines'],
      },
      {
        id: 'eu-technical-screening',
        name: 'Technical Screening Criteria',
        description: 'Meeting sector-specific technical screening criteria',
        status: 'partial',
        score: 45,
        maxScore: 100,
        details: [
          'Risk thresholds aligned with EU standards',
          'Location-specific analysis available',
        ],
        recommendations: ['Map assets to EU Taxonomy activity codes', 'Document technical compliance per activity'],
      },
      {
        id: 'eu-disclosure',
        name: 'Taxonomy Disclosure',
        description: 'Reporting taxonomy-aligned activities',
        status: 'partial',
        score: 50,
        maxScore: 100,
        details: [
          'Portfolio value breakdown available',
          'Risk categorization exportable',
        ],
        recommendations: ['Calculate taxonomy-aligned revenue percentage', 'Prepare Article 8 disclosure template'],
      },
    ];

    const applicableRequirements = requirements.filter(r => r.status !== 'not-applicable');
    const totalScore = applicableRequirements.reduce((sum, r) => sum + r.score, 0);
    const maxScore = applicableRequirements.reduce((sum, r) => sum + r.maxScore, 0);
    const overallScore = maxScore > 0 ? Math.round((totalScore / maxScore) * 100) : 0;

    return {
      id: 'eu-taxonomy',
      name: 'EU Taxonomy',
      fullName: 'EU Taxonomy for Sustainable Activities',
      icon: <EUIcon />,
      description: 'European Union classification system for environmentally sustainable economic activities',
      requirements,
      overallScore,
      deadline: 'Annual reporting (EU entities)',
      mandatory: false,
    };
  }, [complianceMetrics]);

  const frameworks = [tcfdFramework, secFramework, euTaxonomyFramework];

  // ============================================================================
  // OVERALL COMPLIANCE SCORE
  // ============================================================================
  const overallCompliance = useMemo(() => {
    const mandatoryFrameworks = frameworks.filter(f => f.mandatory);
    const avgScore = mandatoryFrameworks.reduce((sum, f) => sum + f.overallScore, 0) / mandatoryFrameworks.length;
    
    return {
      score: Math.round(avgScore),
      compliantCount: frameworks.filter(f => f.overallScore >= 80).length,
      partialCount: frameworks.filter(f => f.overallScore >= 50 && f.overallScore < 80).length,
      nonCompliantCount: frameworks.filter(f => f.overallScore < 50).length,
    };
  }, [frameworks]);

  // ============================================================================
  // RADAR CHART DATA
  // ============================================================================
  const radarData = useMemo(() => {
    return [
      { subject: 'Governance', TCFD: tcfdFramework.requirements.filter(r => r.id.includes('governance')).reduce((s, r) => s + r.score, 0) / 2, SEC: secFramework.requirements.find(r => r.id.includes('governance'))?.score || 0, fullMark: 100 },
      { subject: 'Strategy', TCFD: tcfdFramework.requirements.filter(r => r.id.includes('strategy')).reduce((s, r) => s + r.score, 0) / 2, SEC: secFramework.requirements.find(r => r.id.includes('strategy'))?.score || 0, fullMark: 100 },
      { subject: 'Risk Mgmt', TCFD: tcfdFramework.requirements.filter(r => r.id.includes('risk')).reduce((s, r) => s + r.score, 0) / 2, SEC: secFramework.requirements.find(r => r.id.includes('risk-management'))?.score || 0, fullMark: 100 },
      { subject: 'Metrics', TCFD: tcfdFramework.requirements.filter(r => r.id.includes('metrics')).reduce((s, r) => s + r.score, 0) / 2, SEC: secFramework.requirements.find(r => r.id.includes('metrics'))?.score || 0, fullMark: 100 },
      { subject: 'Disclosure', TCFD: 85, SEC: secFramework.requirements.find(r => r.id.includes('financial'))?.score || 0, fullMark: 100 },
    ];
  }, [tcfdFramework, secFramework]);

  // ============================================================================
  // EXPORT FUNCTIONS
  // ============================================================================
  const generateReport = (format: string) => {
    const reportData = {
      generatedAt: new Date().toISOString(),
      portfolio: {
        name: portfolio.name || 'Portfolio',
        totalValue: totalValue,
        totalLocations: complianceMetrics.totalLocations,
      },
      overallCompliance: overallCompliance,
      frameworks: frameworks.map(f => ({
        id: f.id,
        name: f.fullName,
        score: f.overallScore,
        requirements: f.requirements.map(r => ({
          name: r.name,
          status: r.status,
          score: r.score,
          maxScore: r.maxScore,
          recommendations: r.recommendations,
        })),
      })),
      riskMetrics: {
        averageRisk: complianceMetrics.avgRisk,
        highRiskLocations: complianceMetrics.highRiskCount,
        highRiskValue: complianceMetrics.highRiskValue,
        geographicDiversity: complianceMetrics.geographicDiversity,
      },
    };

    if (format === 'json') {
      const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Regulatory-Compliance-Report-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } else if (format === 'csv') {
      // Generate CSV for requirements
      const rows = [['Framework', 'Requirement', 'Status', 'Score', 'Max Score', 'Percentage']];
      frameworks.forEach(f => {
        f.requirements.forEach(r => {
          rows.push([f.name, r.name, r.status, String(r.score), String(r.maxScore), `${Math.round((r.score / r.maxScore) * 100)}%`]);
        });
      });
      const csv = rows.map(r => r.join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Regulatory-Compliance-Report-${Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    }

    setExportDialog(false);
  };

  // ============================================================================
  // RENDER
  // ============================================================================
  if (items.length === 0) {
    return (
      <Alert severity="info">
        No portfolio data available for compliance assessment. Add locations to generate compliance reports.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Regulatory Compliance Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Climate disclosure readiness across TCFD, SEC, and EU Taxonomy frameworks
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={() => setExportDialog(true)}
          sx={{ bgcolor: '#00D4FF' }}
        >
          Export Reports
        </Button>
      </Box>

      {/* Overall Compliance Score */}
      <Paper sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #F0F9FF 0%, #E0F2FE 100%)' }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{
                width: 140,
                height: 140,
                borderRadius: '50%',
                border: `8px solid ${getScoreColor(overallCompliance.score)}`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                bgcolor: 'white',
                boxShadow: `0 0 30px ${getScoreColor(overallCompliance.score)}30`,
              }}>
                <Typography variant="h2" fontWeight={800} sx={{ color: getScoreColor(overallCompliance.score), lineHeight: 1 }}>
                  {overallCompliance.score}
                </Typography>
                <Typography variant="caption" color="text.secondary">out of 100</Typography>
              </Box>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 2 }}>
                Overall Compliance Score
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={8}>
            <Grid container spacing={2}>
              {frameworks.map((framework) => (
                <Grid item xs={12} sm={4} key={framework.id}>
                  <Card 
                    sx={{ 
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' },
                      border: selectedFramework?.id === framework.id ? `2px solid ${getScoreColor(framework.overallScore)}` : '1px solid #E5E7EB',
                    }}
                    onClick={() => setSelectedFramework(framework)}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                        {framework.icon}
                        <Typography variant="subtitle2" fontWeight={600}>{framework.name}</Typography>
                        {!framework.mandatory && <Chip label="Optional" size="small" variant="outlined" />}
                      </Box>
                      <Typography variant="h4" fontWeight={700} sx={{ color: getScoreColor(framework.overallScore) }}>
                        {framework.overallScore}%
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={framework.overallScore}
                        sx={{
                          mt: 1,
                          height: 6,
                          borderRadius: 3,
                          bgcolor: '#E5E7EB',
                          '& .MuiLinearProgress-bar': { bgcolor: getScoreColor(framework.overallScore), borderRadius: 3 },
                        }}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Overview" icon={<AssessmentIcon />} iconPosition="start" />
          <Tab label="TCFD" icon={<GlobalIcon />} iconPosition="start" />
          <Tab label="SEC Climate" icon={<USIcon />} iconPosition="start" />
          <Tab label="EU Taxonomy" icon={<EUIcon />} iconPosition="start" />
          <Tab label="Action Items" icon={<WarningIcon />} iconPosition="start" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {/* Tab 0: Overview */}
          {activeTab === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Compliance Comparison</Typography>
                <Box sx={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={radarData}>
                      <PolarGrid />
                      <PolarAngleAxis dataKey="subject" />
                      <PolarRadiusAxis angle={30} domain={[0, 100]} />
                      <Radar name="TCFD" dataKey="TCFD" stroke="#2196F3" fill="#2196F3" fillOpacity={0.3} />
                      <Radar name="SEC" dataKey="SEC" stroke="#4CAF50" fill="#4CAF50" fillOpacity={0.3} />
                      <Legend />
                    </RadarChart>
                  </ResponsiveContainer>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Framework Status</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#F9FAFB' }}>
                        <TableCell>Framework</TableCell>
                        <TableCell align="center">Score</TableCell>
                        <TableCell align="center">Status</TableCell>
                        <TableCell>Deadline</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {frameworks.map((framework) => (
                        <TableRow key={framework.id} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {framework.icon}
                              <Box>
                                <Typography variant="body2" fontWeight={500}>{framework.name}</Typography>
                                <Typography variant="caption" color="text.secondary">{framework.fullName}</Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={`${framework.overallScore}%`}
                              size="small"
                              sx={{
                                bgcolor: `${getScoreColor(framework.overallScore)}20`,
                                color: getScoreColor(framework.overallScore),
                                fontWeight: 700,
                              }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            {framework.overallScore >= 80 ? (
                              <Chip label="Ready" size="small" color="success" />
                            ) : framework.overallScore >= 50 ? (
                              <Chip label="In Progress" size="small" color="warning" />
                            ) : (
                              <Chip label="Needs Work" size="small" color="error" />
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{framework.deadline}</Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          )}

          {/* Tab 1-3: Framework Details */}
          {[1, 2, 3].includes(activeTab) && (
            <Box>
              {(() => {
                const framework = activeTab === 1 ? tcfdFramework : activeTab === 2 ? secFramework : euTaxonomyFramework;
                return (
                  <>
                    <Alert severity="info" sx={{ mb: 3 }}>
                      <Typography variant="body2">
                        <strong>{framework.fullName}</strong> - {framework.description}
                      </Typography>
                    </Alert>

                    {framework.requirements.map((req) => (
                      <Accordion key={req.id} sx={{ mb: 1 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
                            {getStatusIcon(req.status)}
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="subtitle2" fontWeight={600}>{req.name}</Typography>
                              <Typography variant="caption" color="text.secondary">{req.description}</Typography>
                            </Box>
                            <Chip
                              label={`${req.score}/${req.maxScore}`}
                              size="small"
                              sx={{
                                bgcolor: `${getScoreColor((req.score / req.maxScore) * 100)}20`,
                                color: getScoreColor((req.score / req.maxScore) * 100),
                                fontWeight: 600,
                              }}
                            />
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <Typography variant="subtitle2" gutterBottom>Current Status</Typography>
                              <List dense>
                                {req.details.map((detail, i) => (
                                  <ListItem key={i}>
                                    <ListItemIcon sx={{ minWidth: 32 }}>
                                      <CheckIcon color="success" fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText primary={detail} />
                                  </ListItem>
                                ))}
                              </List>
                            </Grid>
                            {req.recommendations.length > 0 && (
                              <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" gutterBottom>Recommendations</Typography>
                                <List dense>
                                  {req.recommendations.map((rec, i) => (
                                    <ListItem key={i}>
                                      <ListItemIcon sx={{ minWidth: 32 }}>
                                        <WarningIcon color="warning" fontSize="small" />
                                      </ListItemIcon>
                                      <ListItemText primary={rec} />
                                    </ListItem>
                                  ))}
                                </List>
                              </Grid>
                            )}
                          </Grid>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </>
                );
              })()}
            </Box>
          )}

          {/* Tab 4: Action Items */}
          {activeTab === 4 && (
            <Box>
              <Typography variant="h6" gutterBottom>Priority Action Items</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Recommendations to improve compliance scores across all frameworks
              </Typography>

              {frameworks.flatMap(f => 
                f.requirements
                  .filter(r => r.recommendations.length > 0)
                  .flatMap(r => r.recommendations.map(rec => ({
                    framework: f.name,
                    requirement: r.name,
                    recommendation: rec,
                    priority: r.score < 50 ? 'High' : r.score < 75 ? 'Medium' : 'Low',
                  })))
              )
              .sort((a, b) => {
                const priorityOrder = { High: 0, Medium: 1, Low: 2 };
                return priorityOrder[a.priority as keyof typeof priorityOrder] - priorityOrder[b.priority as keyof typeof priorityOrder];
              })
              .map((item, index) => (
                <Card key={index} sx={{ mb: 2 }}>
                  <CardContent sx={{ py: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <Chip
                        label={item.priority}
                        size="small"
                        color={item.priority === 'High' ? 'error' : item.priority === 'Medium' ? 'warning' : 'success'}
                      />
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2" fontWeight={500}>{item.recommendation}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.framework} → {item.requirement}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}

              {frameworks.flatMap(f => f.requirements.filter(r => r.recommendations.length > 0)).length === 0 && (
                <Alert severity="success">
                  All compliance requirements are being met. No immediate action items.
                </Alert>
              )}
            </Box>
          )}
        </Box>
      </Paper>

      {/* Export Dialog */}
      <Dialog open={exportDialog} onClose={() => setExportDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Export Compliance Reports</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Generate regulatory-ready compliance reports for your portfolio.
          </Typography>

          <Typography variant="subtitle2" gutterBottom>Include Frameworks</Typography>
          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={<Checkbox checked={exportOptions.tcfd} onChange={(e) => setExportOptions({ ...exportOptions, tcfd: e.target.checked })} />}
              label="TCFD Report"
            />
            <FormControlLabel
              control={<Checkbox checked={exportOptions.sec} onChange={(e) => setExportOptions({ ...exportOptions, sec: e.target.checked })} />}
              label="SEC Climate Disclosure"
            />
            <FormControlLabel
              control={<Checkbox checked={exportOptions.euTaxonomy} onChange={(e) => setExportOptions({ ...exportOptions, euTaxonomy: e.target.checked })} />}
              label="EU Taxonomy Assessment"
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" gutterBottom>Export Format</Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant={exportOptions.format === 'json' ? 'contained' : 'outlined'}
              onClick={() => setExportOptions({ ...exportOptions, format: 'json' })}
              startIcon={<ReportIcon />}
            >
              JSON
            </Button>
            <Button
              variant={exportOptions.format === 'csv' ? 'contained' : 'outlined'}
              onClick={() => setExportOptions({ ...exportOptions, format: 'csv' })}
              startIcon={<ReportIcon />}
            >
              CSV
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => generateReport(exportOptions.format)} startIcon={<DownloadIcon />}>
            Generate Report
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}